import {
  IconDental,
  IconEyeglass,
  IconInfoCircle,
  IconStethoscope,
} from '@tabler/icons-react';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import { Grid, Group, Icon, Space, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import Decimal from 'decimal.js';

import { AllowanceBasedTooltip } from '../AllowanceTooltip';
import { EnrollmentReviewCardCurrency } from '../EnrollmentReviewCard/EnrollmentReviewCardCurrency';
import { EnrollmentAllowanceCardItem } from './EnrollmentAllowanceCardItem';
import { useEnrollmentAllowanceStore } from './EnrollmentAllowanceStore';

const gridWidth = '17rem';

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  highlightedBenefitId?: string;
  withOnboardingAllowance?: boolean;
};

export const EnrollmentAllowanceCard = ({
  employeeId,
  onboardingPeriodId,
  highlightedBenefitId,
  withOnboardingAllowance = false,
}: Props) => {
  const medicalPlans = useEnrollmentAllowanceStore(
    (state) => state.medicalPlans
  );
  const dentalPlans = useEnrollmentAllowanceStore((state) => state.dentalPlans);
  const visionPlans = useEnrollmentAllowanceStore((state) => state.visionPlans);
  const familyUnit = useEnrollmentAllowanceStore((state) => state.familyUnit);
  const numberOfDependents = useEnrollmentAllowanceStore(
    (state) => state.numberOfDependents
  );

  const { data: possibleEmployerContribution = [] } = useMonolithQuery({
    method:
      'contributionModelsControllerFindPossibleEmployerContributionsByAnyPeriod',
    params: [onboardingPeriodId],
  });

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
    enabled: withOnboardingAllowance,
  });

  const premium = [...medicalPlans, ...dentalPlans, ...visionPlans]
    .reduce((sum, plan) => {
      return sum.add(new Decimal(plan.premium));
    }, new Decimal(0))
    .toNumber();

  const stipend =
    possibleEmployerContribution.find(
      (employerContribution) =>
        employerContribution.familyUnit === familyUnit &&
        employerContribution.numberOfDependents === numberOfDependents
    )?.stipend ?? 0;

  const allowance = withOnboardingAllowance
    ? (onboardingPeriod?.allowance ?? stipend)
    : stipend;

  const employerContribution = Math.min(allowance, premium);
  const employeeContribution = Math.max(
    new Decimal(premium).sub(allowance).toNumber(),
    0
  );

  const balance = Decimal.sub(allowance, premium).toNumber();

  const withPlanDetails =
    onboardingPeriod?.enrollmentIsDentalEnabled ||
    onboardingPeriod?.enrollmentIsVisionEnabled;

  return (
    <Grid
      bg="var(--color-background-general-note-primary)"
      miw={gridWidth}
      maw={gridWidth}
      pos="sticky"
      gap={0}
      bdrs="sm"
      p="lg"
    >
      <Grid.Col span={8}>
        <Group gap="0.25rem">
          <Text fw={600}>Total allowance</Text>
          <Tooltip
            label={
              <AllowanceBasedTooltip
                possibleEmployerContributions={possibleEmployerContribution}
              />
            }
          >
            <Icon
              style={{ cursor: 'pointer' }}
              icon={IconInfoCircle}
              size="1rem"
            />
          </Tooltip>
        </Group>
      </Grid.Col>
      <Grid.Col span={4}>
        <EnrollmentReviewCardCurrency fw={600}>
          {allowance}
        </EnrollmentReviewCardCurrency>
      </Grid.Col>
      {withPlanDetails && (
        <>
          <Grid.Col>
            <Space h="sm" />
          </Grid.Col>
          {medicalPlans.map(({ planId, carrierName, premium }) => (
            <EnrollmentAllowanceCardItem
              name={carrierName || 'Medical'}
              icon={IconStethoscope}
              premium={premium}
              key={planId}
              isHighlighted={planId === highlightedBenefitId}
            />
          ))}
          {dentalPlans.map(({ planId, carrierName, premium }) => (
            <EnrollmentAllowanceCardItem
              name={carrierName || 'Dental'}
              premium={premium}
              icon={IconDental}
              key={planId}
              isHighlighted={planId === highlightedBenefitId}
            />
          ))}
          {visionPlans.map(({ planId, carrierName, premium }) => (
            <EnrollmentAllowanceCardItem
              name={carrierName || 'Vision'}
              icon={IconEyeglass}
              premium={premium}
              key={planId}
              isHighlighted={planId === highlightedBenefitId}
            />
          ))}
          <Grid.Col mt="sm">
            <Divider />
          </Grid.Col>
        </>
      )}
      <Grid.Col span={8} my="sm">
        <Text fw={600}>Total premium</Text>
      </Grid.Col>
      <Grid.Col span={4} my="sm">
        <EnrollmentReviewCardCurrency fw={600}>
          {premium}
        </EnrollmentReviewCardCurrency>
      </Grid.Col>
      <Grid.Col span={8} mb="sm">
        <Text size="sm" c="var(--color-text-default-secondary)">
          Balance
        </Text>
      </Grid.Col>
      <Grid.Col span={4} mb="sm">
        <EnrollmentReviewCardCurrency style={{ textWrap: 'nowrap' }}>
          {balance}
        </EnrollmentReviewCardCurrency>
      </Grid.Col>
      <Grid.Col>
        <Divider />
      </Grid.Col>
      <Grid.Col span={8} mt="sm">
        <Text size="sm" c="var(--color-text-default-secondary)">
          Employee contribution
        </Text>
      </Grid.Col>
      <Grid.Col span={4} mt="sm">
        <EnrollmentReviewCardCurrency>
          {employeeContribution}
        </EnrollmentReviewCardCurrency>
      </Grid.Col>
      <Grid.Col span={8} mt="0.25rem">
        <Text size="sm" c="var(--color-text-default-secondary)">
          Employer contribution
        </Text>
      </Grid.Col>
      <Grid.Col span={4} mt="0.25rem">
        <EnrollmentReviewCardCurrency>
          {employerContribution}
        </EnrollmentReviewCardCurrency>
      </Grid.Col>
    </Grid>
  );
};
